

import React, { useState, useEffect } from 'react';
import {
  FileTextOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  EyeOutlined,
  SaveOutlined,
  SettingOutlined,
  UserOutlined,
  CalendarOutlined,
  CheckOutlined,
  SearchOutlined,
  FilterOutlined,
  DownloadOutlined,
  QrcodeOutlined,
  PictureOutlined,
  FontSizeOutlined,
  HighlightOutlined,
  ArrowDownOutlined,
  ArrowRightOutlined,
  ExclamationCircleOutlined,
  BgColorsOutlined,
  LockOutlined,
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined
} from '@ant-design/icons';

import {
  Layout,
  Card,
  Button,
  Input,
  Table,
  Tag,
  Progress,
  Modal,
  Steps,
  Radio,
  Checkbox,
  Select,
  DatePicker,
  Tabs,
  Row,
  Col,
  Statistic,
  List,
  Avatar,
  Divider,
  Space,
  Tooltip,
  Form,
  InputNumber,
  ColorPicker,
  message,
  Typography,
  Descriptions
} from 'antd';
import TemplateDesigner from './TemplateDesign';
import { getTicketTemplateApi } from '../../../redux/api/api';
import BadgeTemplatesGallery from './BadgeTemplatesGallery';

const { Header, Content } = Layout;
const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Step } = Steps;
const { Title, Text } = Typography;

const TicketManagementUI = ({ onTicketDataChange, initialTicketClasses = [] }) => {
  const [activeView, setActiveView] = useState('classes');
  const [showTemplateDesigner, setShowTemplateDesigner] = useState(false);
  const [showClassModal, setShowClassModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTicketClass, setSelectedTicketClass] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [form] = Form.useForm();
  const [assignmentType, setAssignmentType] = useState('role_based');
  const [ticketClasses, setTicketClasses] = useState(initialTicketClasses);
  // console.log('Initial Ticket Classes:', ticketClasses);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch templates
  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const { data } = await getTicketTemplateApi();
      if (data.success) {
        setTemplates(data.templates);
      } else {
        console.error('Failed to fetch templates:', data.message);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Template handlers
  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setShowTemplateDesigner(true);
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate(null);
    setShowTemplateDesigner(true);
  };

  const handleTemplateDesignerClose = () => {
    setShowTemplateDesigner(false);
    setSelectedTemplate(null);
    fetchTemplates();
  };

  // Ticket Class CRUD Operations
  const handleCreateTicketClass = (formData) => {
    const selectedTemplate = templates.find(t => t._id === formData.templateId);

    const newTicketClass = {
      id: Date.now().toString(),
      name: formData.name,
      description: formData.description,
      color: formData.color,
      templateName: selectedTemplate?.name || 'No Template',
      templateId: formData.templateId,
      assignmentType: formData.assignmentRule?.type || 'role_based',
      roles: formData.assignmentRule?.roles || [],
      programs: formData.assignmentRule?.programs || [],
      grantAllPrograms: formData.assignmentRule?.grantAllPrograms || false,
      price: formData.price || 0,
      quantity: formData.quantity || 100,
      sold: 0,
      status: 'active',
      salesSettings: formData.salesSettings || {},
      benefits: formData.benefits || [],
      requireApproval: formData.requireApproval || false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const updatedClasses = [...ticketClasses, newTicketClass];
    setTicketClasses(updatedClasses);
    if (onTicketDataChange) {
      onTicketDataChange(updatedClasses);
    }
    message.success('Ticket class created successfully');
    setShowClassModal(false);
    resetForm();
  };

  const handleEditTicketClass = (formData) => {
    if (!selectedTicketClass) return;

    const selectedTemplate = templates.find(t => t._id === formData.templateId);

    const updatedClasses = ticketClasses.map(tc =>
      tc.id === selectedTicketClass.id
        ? {
          ...tc,
          name: formData.name,
          description: formData.description,
          color: formData.color,
          templateName: selectedTemplate?.name || 'No Template',
          templateId: formData.templateId,
          assignmentType: formData.assignmentRule?.type || 'role_based',
          roles: formData.assignmentRule?.roles || [],
          programs: formData.assignmentRule?.programs || [],
          grantAllPrograms: formData.assignmentRule?.grantAllPrograms || false,
          price: formData.price || 0,
          quantity: formData.quantity || 100,
          salesSettings: formData.salesSettings || {},
          benefits: formData.benefits || [],
          requireApproval: formData.requireApproval || false,
          updatedAt: new Date().toISOString()
        }
        : tc
    );

    setTicketClasses(updatedClasses);
    if (onTicketDataChange) {
      onTicketDataChange(updatedClasses);
    }
    message.success('Ticket class updated successfully');
    setShowClassModal(false);
    setSelectedTicketClass(null);
    setIsEditing(false);
    resetForm();
  };

  const handleViewTicketClass = (ticketClass) => {
    setSelectedTicketClass(ticketClass);
    setShowViewModal(true);
  };

  const handleDeleteTicketClass = (ticketClass) => {
    Modal.confirm({
      title: 'Delete Ticket Class',
      content: `Are you sure you want to delete "${ticketClass.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        const updatedClasses = ticketClasses.filter(tc => tc.id !== ticketClass.id);
        setTicketClasses(updatedClasses);
        if (onTicketDataChange) {
          onTicketDataChange(updatedClasses);
        }
        message.success('Ticket class deleted successfully');
      }
    });
  };

  const handleEditClick = (ticketClass) => {
    setSelectedTicketClass(ticketClass);
    setIsEditing(true);
    setShowClassModal(true);

    // Pre-fill form with existing data
    form.setFieldsValue({
      name: ticketClass.name,
      description: ticketClass.description,
      color: ticketClass.color,
      templateId: ticketClass.templateId,
      price: ticketClass.price,
      quantity: ticketClass.quantity,
      assignmentRule: {
        type: ticketClass.assignmentType,
        roles: ticketClass.roles,
        programs: ticketClass.programs,
        grantAllPrograms: ticketClass.grantAllPrograms
      },
      salesSettings: ticketClass.salesSettings,
      benefits: ticketClass.benefits,
      requireApproval: ticketClass.requireApproval
    });
    setAssignmentType(ticketClass.assignmentType);
  };

  const handleCreateClick = () => {
    setSelectedTicketClass(null);
    setIsEditing(false);
    setShowClassModal(true);
    resetForm();
  };

  const resetForm = () => {
    form.resetFields();
    setAssignmentType('role_based');
  };

  const handleModalClose = () => {
    setShowClassModal(false);
    setSelectedTicketClass(null);
    setIsEditing(false);
    resetForm();
  };

  // Filtered ticket classes based on search
  const filteredTicketClasses = ticketClasses?.filter(tc => {
    if (!tc) return false;

    const name = tc.name || '';
    const templateName = tc.templateName || '';
    const searchTerm = searchQuery.toLowerCase();

    return name.toLowerCase().includes(searchTerm) ||
      templateName.toLowerCase().includes(searchTerm);
  }) || [];


  // Ticket Classes View Component
  const TicketClasses = () => {
    const columns = [
      {
        title: 'Ticket Class',
        dataIndex: 'name',
        key: 'name',
        render: (text, record) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {record.color && (
              <div
                style={{
                  width: 16,
                  height: 16,
                  backgroundColor: record.color,
                  borderRadius: 4,
                  border: '1px solid #d9d9d9'
                }}
              />
            )}
            <div style={{ fontWeight: 500 }}>{text}</div>
          </div>
        )
      },
      {
        title: 'Template',
        dataIndex: 'templateName',
        key: 'templateName',
        render: (text) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileTextOutlined style={{ color: '#999' }} />
            <span>{text}</span>
          </div>
        )
      },
      {
        title: 'Assignment',
        key: 'assignment',
        render: (record) => (
          <div>
            {record.assignmentType === 'role_based' ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#666', marginBottom: 8 }}>
                  <UserOutlined />
                  <span style={{ fontSize: '12px' }}>Role-based</span>
                </div>
                <div>
                  {record.roles?.map((role, idx) => (
                    <Tag key={idx} color="purple" style={{ marginBottom: 4 }}>{role}</Tag>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#666', marginBottom: 8 }}>
                  <CalendarOutlined />
                  <span style={{ fontSize: '12px' }}>Program-based</span>
                </div>
                <div>
                  {record.programs?.map((program, idx) => (
                    <Tag key={idx} color="blue" style={{ marginBottom: 4 }}>{program}</Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        )
      },
      {
        title: 'Price',
        dataIndex: 'price',
        key: 'price',
        render: (price) => (
          <span style={{ fontWeight: 500 }}>
            {price === 0 ? 'Free' : `₹${price}`}
          </span>
        )
      },
      {
        title: 'Sales',
        key: 'sales',
        render: (record) => (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>{record.sold}/{record.quantity}</span>
              <span>{Math.round((record.sold / record.quantity) * 100)}%</span>
            </div>
            <Progress
              percent={Math.round((record.sold / record.quantity) * 100)}
              size="small"
              showInfo={false}
            />
          </div>
        )
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status) => (
          <Tag color={status === 'active' ? 'green' : 'red'}>{status}</Tag>
        )
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (record) => (
          <Space>
            <Tooltip title="View">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => handleViewTicketClass(record)}
              />
            </Tooltip>
            <Tooltip title="Edit">
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleEditClick(record)}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                onClick={() => handleDeleteTicketClass(record)}
              />
            </Tooltip>
          </Space>
        )
      }
    ];

    return (
      <div style={{ padding: '24px 0' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
          <Col>
            <Space>
              <Input
                placeholder="Search ticket classes..."
                prefix={<SearchOutlined />}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: 300 }}
              />
              <Button icon={<FilterOutlined />}>
                Filter
              </Button>
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateClick}
              size="large"
            >
              Create Ticket Class
            </Button>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredTicketClasses}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          loading={loading}
        />
      </div>
    );
  };

  // View Ticket Class Modal
  const ViewTicketClassModal = () => (
    <Modal
      title={`Ticket Class: ${selectedTicketClass?.name}`}
      open={showViewModal}
      onCancel={() => setShowViewModal(false)}
      footer={[
        <Button key="edit" type="primary" onClick={() => {
          setShowViewModal(false);
          handleEditClick(selectedTicketClass);
        }}>
          Edit
        </Button>,
        <Button key="close" onClick={() => setShowViewModal(false)}>
          Close
        </Button>
      ]}
      width={700}
    >
      {selectedTicketClass && (
        <Descriptions bordered column={2}>
          <Descriptions.Item label="Name" span={2}>
            {selectedTicketClass.name}
          </Descriptions.Item>
          <Descriptions.Item label="Description">
            {selectedTicketClass.description || 'No description'}
          </Descriptions.Item>
          <Descriptions.Item label="Template">
            {selectedTicketClass.templateName}
          </Descriptions.Item>
          <Descriptions.Item label="Price">
            {selectedTicketClass.price === 0 ? 'Free' : `₹${selectedTicketClass.price}`}
          </Descriptions.Item>
          <Descriptions.Item label="Quantity">
            {selectedTicketClass.quantity}
          </Descriptions.Item>
          <Descriptions.Item label="Sold">
            {selectedTicketClass.sold}
          </Descriptions.Item>
          <Descriptions.Item label="Assignment Type">
            {selectedTicketClass.assignmentType}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={selectedTicketClass.status === 'active' ? 'green' : 'red'}>
              {selectedTicketClass.status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Requires Approval" span={2}>
            {selectedTicketClass.requireApproval ? 'Yes' : 'No'}
          </Descriptions.Item>
          {selectedTicketClass.roles && selectedTicketClass.roles.length > 0 && (
            <Descriptions.Item label="Roles" span={2}>
              {selectedTicketClass.roles.map((role, idx) => (
                <Tag key={idx} color="purple" style={{ marginBottom: 4 }}>{role}</Tag>
              ))}
            </Descriptions.Item>
          )}
          {selectedTicketClass.programs && selectedTicketClass.programs.length > 0 && (
            <Descriptions.Item label="Programs" span={2}>
              {selectedTicketClass.programs.map((program, idx) => (
                <Tag key={idx} color="blue" style={{ marginBottom: 4 }}>{program}</Tag>
              ))}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Created" span={2}>
            {new Date(selectedTicketClass.createdAt).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated" span={2}>
            {new Date(selectedTicketClass.updatedAt).toLocaleString()}
          </Descriptions.Item>
        </Descriptions>
      )}
    </Modal>
  );

  // Template Library Component
  const TemplateLibrary = () => (
    <div style={{ padding: '24px 0' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col flex="auto">
          <Input
            placeholder="Search templates..."
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="large"
          />
        </Col>
        <Col>
          <Button icon={<FilterOutlined />} size="large">
            Filter
          </Button>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreateTemplate}
            size="large"
          >
            Create Template
          </Button>
        </Col>
      </Row>

      <BadgeTemplatesGallery
        onEditTemplate={handleEditTemplate}
        onDuplicateTemplate={() => { }}
        onDeleteTemplate={() => { }}
      />
    </div>
  );

  const CreateTicketClassModal = ({ onCreateTicketClass, onCancel }) => {
    const [step, setStep] = useState(1);
    const [formError, setFormError] = useState('');
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
      // Step 1: Basic Information
      name: '',
      description: '',
      templateId: '',
      price: 0,
      quantity: 100,

      // Step 2: Assignment Rules
      assignmentRule: {
        type: 'role_based',
        roles: [],
        programs: [],
        grantAllPrograms: false
      },

      // Step 3: Additional Settings
      salesSettings: {
        visibility: 'always',
        minPurchase: 1,
        maxPurchase: 5,
        startDate: null,
        endDate: null,
        showWhenSoldOut: false,
        enableWaitlist: false
      },
      benefits: [],
      requireApproval: false
    });

    const updateFormData = (field, value) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    };

    const updateNestedFormData = (parent, updates) => {
      setFormData(prev => ({
        ...prev,
        [parent]: { ...prev[parent], ...updates }
      }));
    };


    const validateStep = () => {
      if (step === 1) {
        if (!formData.name.trim()) return { isValid: false, error: 'Name is required' };
        // if (!formData.templateId) return { isValid: false, error: 'Template is required' };
      }
      if (step === 2) {
        if (formData.assignmentRule.type === 'role_based' && formData.assignmentRule.roles.length === 0) {
          // return { isValid: false, error: 'Select at least one role' };
        }
      }
      return { isValid: true };
    };

    const resetForm = () => {
      setFormData({
        name: '',
        description: '',
        templateId: '',
        price: 0,
        quantity: 100,
        assignmentRule: {
          type: 'role_based',
          roles: [],
          programs: [],
          grantAllPrograms: false
        },
        salesSettings: {
          visibility: 'always',
          minPurchase: 1,
          maxPurchase: 5,
          startDate: null,
          endDate: null,
          showWhenSoldOut: false,
          enableWaitlist: false
        },
        benefits: [],
        requireApproval: false
      });
    };

    const handleNext = () => {
      const validation = validateStep(step);
      if (!validation.isValid) {
        setFormError(validation.error);
        return;
      }
      setFormError('');
      setStep(step + 1);
    };

    const handleBack = () => {
      if (step > 1) {
        setStep(step - 1);
        setFormError('');
      } else {
        handleCancel();
      }
    };

    const handleCancel = () => {
      setShowClassModal(false);
      setStep(1);
      setFormError('');
      resetForm();
    };

    const handleSubmit = () => {
      const validation = validateStep();
      if (!validation.isValid) {
        setFormError(validation.error);
        return;
      }
      handleCreateTicketClass(formData);
      setShowClassModal(false);
      setStep(1);
      setFormError('');
    };



    const steps = [
      {
        title: 'Basic Info',
        content: (
          <Form layout="vertical">
            <Form.Item label="Ticket Name" required>
              <Input
                placeholder="Enter ticket name"
                value={formData.name}
                onChange={(e) => updateFormData('name', e.target.value)}
                size="large"
              />
            </Form.Item>

            <Form.Item label="Color">
              <ColorPicker
                value={formData.color}
                onChange={(color) => updateFormData('color', color.toHexString())}
                showText
                style={{ width: '100%' }}
              />
            </Form.Item>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Quantity *</label>
              <InputNumber
                value={formData.quantity}
                onChange={(value) => updateFormData('quantity', value || 0)}
                style={{ width: '100%' }}
                min={1}
                size="large"
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Price</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Radio.Group
                  value={formData.price === 0 ? 'free' : 'paid'}
                  onChange={(e) => updateFormData('price', e.target.value === 'free' ? 0 : 100)}
                >
                  <Radio value="free">Free</Radio>
                  <Radio value="paid">Paid</Radio>
                </Radio.Group>
                {formData.price > 0 && (
                  <InputNumber
                    value={formData.price}
                    onChange={(value) => updateFormData('price', value || 0)}
                    prefix="₹"
                    style={{ width: 120 }}
                  />
                )}
              </div>
            </div>

            <Form.Item label="Select Template" >
              <Select
                placeholder="Choose a template..."
                value={formData.templateId}
                onChange={(value) => updateFormData('templateId', value)}
                size="large"
              >
                {templates.map(t => (
                  <Option key={t.id} value={t.id}>{t.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        )
      },
      {
        title: 'Settings',
        content: (
          <Form layout="vertical">
            {/* General Visibility Offers Section */}
            <div style={{
              background: '#f8f9fa',
              padding: 16,
              borderRadius: 8,
              marginBottom: 16,
              border: '1px solid #e9ecef'
            }}>
              <h4 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <EyeOutlined />
                General Visibility Offers 😊
              </h4>

              <Form.Item label="Ticket Visibility">
                <Radio.Group
                  value={formData.salesSettings.visibility}
                  onChange={(e) => updateNestedFormData('salesSettings', { visibility: e.target.value })}
                >
                  <Space direction="vertical">
                    <Radio value="always">Always Visible</Radio>
                    <Radio value="hidden">Always Hidden</Radio>
                    <Radio value="conditional">Hide When</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>

              {formData.salesSettings.visibility === 'conditional' && (
                <Form.Item>
                  <Checkbox.Group
                    value={formData.salesSettings.visibilityOptions}
                    onChange={(values) => updateNestedFormData('salesSettings', { visibilityOptions: values })}
                  >
                    <Space direction="vertical">
                      <Checkbox value="salesNotStarted">Ticket sales haven't begun</Checkbox>
                      <Checkbox value="soldOut">Ticket sales are sold out or marked as sold out</Checkbox>
                      <Checkbox value="salesEnded">Ticket sales have ended</Checkbox>
                      <Checkbox value="statusClosed">Ticket class status is closed</Checkbox>
                    </Space>
                  </Checkbox.Group>
                </Form.Item>
              )}
            </div>

            {/* Registration Approval Section */}
            <div style={{
              background: '#f8f9fa',
              padding: 16,
              borderRadius: 8,
              marginBottom: 16,
              border: '1px solid #e9ecef'
            }}>
              <h4 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <UserOutlined />
                Registration approval 💬
              </h4>
              <p style={{ color: '#666', marginBottom: 12 }}>
                You can pre-screen the order request placed by the visitors and manually approve or deny them before issuing their tickets.
              </p>
              <Checkbox
                checked={formData.salesSettings.requireApproval}
                onChange={(e) => updateNestedFormData('salesSettings', { requireApproval: e.target.checked })}
              >
                Require approval for registration
              </Checkbox>
            </div>

            {/* Purchase Limits */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Minimum Buying Limit *">
                  <InputNumber
                    value={formData.salesSettings.minPurchase}
                    onChange={(value) => updateNestedFormData('salesSettings', { minPurchase: value || 1 })}
                    style={{ width: '100%' }}
                    min={1}
                    size="large"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Maximum Buying Limit *">
                  <InputNumber
                    value={formData.salesSettings.maxPurchase}
                    onChange={(value) => updateNestedFormData('salesSettings', { maxPurchase: value || 1 })}
                    style={{ width: '100%' }}
                    min={1}
                    size="large"
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Sales Dates */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Sales Start Date">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <DatePicker
                      value={formData.salesSettings.startDate}
                      onChange={(date) => updateNestedFormData('salesSettings', { startDate: date })}
                      style={{ flex: 1 }}
                      placeholder="Select start date"
                    />
                    <Radio.Group
                      value={formData.salesSettings.startDate ? 'custom' : 'now'}
                      onChange={(e) => updateNestedFormData('salesSettings', {
                        startDate: e.target.value === 'now' ? new Date() : null
                      })}
                    >
                      <Radio value="now">Now</Radio>
                    </Radio.Group>
                  </div>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Sales End Date">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <DatePicker
                      value={formData.salesSettings.endDate}
                      onChange={(date) => updateNestedFormData('salesSettings', { endDate: date })}
                      style={{ flex: 1 }}
                      placeholder="Select end date"
                    />
                    <Radio.Group
                      value={formData.salesSettings.endDate ? 'custom' : 'eventEnd'}
                      onChange={(e) => updateNestedFormData('salesSettings', {
                        endDate: e.target.value === 'eventEnd' ? null : null
                      })}
                    >
                      <Radio value="eventEnd">Event End Date</Radio>
                    </Radio.Group>
                  </div>
                </Form.Item>
              </Col>
            </Row>

            {/* Sales Times */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Start Time">
                  <Select
                    value={formData.salesSettings.startTime}
                    onChange={(value) => updateNestedFormData('salesSettings', { startTime: value })}
                    size="large"
                  >
                    <Option value="12:00 AM">12:00 AM</Option>
                    <Option value="1:00 AM">1:00 AM</Option>
                    <Option value="2:00 AM">2:00 AM</Option>
                    <Option value="3:00 AM">3:00 AM</Option>
                    <Option value="4:00 AM">4:00 AM</Option>
                    <Option value="5:00 AM">5:00 AM</Option>
                    <Option value="6:00 AM">6:00 AM</Option>
                    <Option value="7:00 AM">7:00 AM</Option>
                    <Option value="8:00 AM">8:00 AM</Option>
                    <Option value="9:00 AM">9:00 AM</Option>
                    <Option value="10:00 AM">10:00 AM</Option>
                    <Option value="11:00 AM">11:00 AM</Option>
                    <Option value="12:00 PM">12:00 PM</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="End Time">
                  <Select
                    value={formData.salesSettings.endTime}
                    onChange={(value) => updateNestedFormData('salesSettings', { endTime: value })}
                    size="large"
                  >
                    <Option value="12:00 PM">12:00 PM</Option>
                    <Option value="1:00 PM">1:00 PM</Option>
                    <Option value="2:00 PM">2:00 PM</Option>
                    <Option value="3:00 PM">3:00 PM</Option>
                    <Option value="4:00 PM">4:00 PM</Option>
                    <Option value="5:00 PM">5:00 PM</Option>
                    <Option value="6:00 PM">6:00 PM</Option>
                    <Option value="7:00 PM">7:00 PM</Option>
                    <Option value="8:00 PM">8:00 PM</Option>
                    <Option value="9:00 PM">9:00 PM</Option>
                    <Option value="10:00 PM">10:00 PM</Option>
                    <Option value="11:00 PM">11:00 PM</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Description */}
            <Form.Item label="Description">
              <TextArea
                rows={4}
                placeholder="Enter ticket description..."
                value={formData.description}
                onChange={(e) => updateFormData('description', e.target.value)}
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: 6
                }}
              />
              <div style={{ marginTop: 8, display: 'flex', gap: 4 }}>
                <Button type="text" icon={<BoldOutlined />} />
                <Button type="text" icon={<ItalicOutlined />} />
                <Button type="text" icon={<UnderlineOutlined />} />
                <Button type="text" icon={<StrikethroughOutlined />} />
                <Button type="text" icon={<FontSizeOutlined />} />
                <Button type="text" icon={<BgColorsOutlined />} />
              </div>
            </Form.Item>
          </Form>
        )
      },
      {
        title: 'Assign',
        content: (
          <Form layout="vertical">
            <Form.Item label="How should this ticket be assigned?" required>
              <Radio.Group
                value={assignmentType}
                onChange={(e) => setAssignmentType(e.target.value)}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Radio value="role_based" style={{ width: '100%', padding: 16, border: '1px solid #d9d9d9', borderRadius: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <UserOutlined style={{ color: '#1890ff', fontSize: 16, marginTop: 2 }} />
                      <div>
                        <div style={{ fontWeight: 500 }}>Role-Based</div>
                        <div style={{ color: '#666', fontSize: 12 }}>
                          Grant access to all programs for specific roles (e.g., VIP, Speaker, Staff)
                        </div>
                      </div>
                    </div>
                  </Radio>
                  <Radio value="program_based" style={{ width: '100%', padding: 16, border: '1px solid #d9d9d9', borderRadius: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <CalendarOutlined style={{ color: '#52c41a', fontSize: 16, marginTop: 2 }} />
                      <div>
                        <div style={{ fontWeight: 500 }}>Program-Based</div>
                        <div style={{ color: '#666', fontSize: 12 }}>
                          Grant access to specific programs/sessions only
                        </div>
                      </div>
                    </div>
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>

            {assignmentType === 'role_based' && (
              <Form.Item label="Select Roles" required>
                <Checkbox.Group style={{ width: '100%' }}>
                  <Row gutter={[8, 8]}>
                    {['VIP', 'Speaker', 'Sponsor', 'Staff', 'Volunteer', 'Guest'].map(role => (
                      <Col span={8} key={role}>
                        <Checkbox value={role}>{role}</Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Checkbox.Group>
                <Checkbox style={{ marginTop: 16 }}>
                  Grant access to all programs in the event
                </Checkbox>
              </Form.Item>
            )}

            {assignmentType === 'program_based' && (
              <Form.Item label="Select Programs" required>
                <Checkbox.Group style={{ width: '100%' }}>
                  <Row gutter={[8, 8]}>
                    {['Main Conference', 'Workshop A', 'Workshop B', 'Networking Dinner', 'Closing Ceremony'].map(program => (
                      <Col span={12} key={program}>
                        <Checkbox value={program}>{program}</Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Checkbox.Group>
              </Form.Item>
            )}
          </Form>
        )
      },
    ];


    return (
      <Modal
        title="Create Ticket Class"
        open={showClassModal}
        onCancel={() => {
          setShowClassModal(false);
          setStep(1);
          setFormError('');
        }}
        width={800}
        footer={[
          <Button key="back" onClick={() => step > 1 ? setStep(step - 1) : setShowClassModal(false)}>
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>,
          <Button
            key="next"
            type="primary"
            onClick={step < 3 ? handleNext : handleSubmit}
          >
            {step === 3 ? 'Create' : 'Next'}
          </Button>
        ]}
      >
        <Steps current={step - 1} style={{ marginBottom: 24 }}>
          {steps.map(item => (
            <Step key={item.title} title={item.title} />
          ))}
        </Steps>
        {formError && (
          <div style={{ color: '#ff4d4f', marginBottom: 16 }}>{formError}</div>
        )}
        <div style={{ minHeight: 400 }}>
          {steps[step - 1].content}
        </div>
      </Modal>
    );
  };


  return (
    <Layout style={{ minHeight: '20vh', background: '#f5f5f5' }}>
      {/* Navigation Tabs */}
      <div style={{ background: '#fff', borderBottom: '1px solid #f0f0f0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
          <Tabs
            activeKey={activeView}
            onChange={setActiveView}
            items={[
              {
                key: 'classes',
                label: (
                  <span>
                    <SettingOutlined style={{ marginRight: 8 }} />
                    Ticket Classes
                  </span>
                )
              },
              {
                key: 'templates',
                label: (
                  <span>
                    <FileTextOutlined style={{ marginRight: 8 }} />
                    Ticket Templates
                  </span>
                )
              },
            ]}
          />
        </div>
      </div>

      {/* Main Content */}
      <Content style={{ padding: '0 24px', maxWidth: 1200, margin: '0 auto', width: '100%' }}>
        {activeView === 'classes' && <TicketClasses />}
        {activeView === 'templates' && <TemplateLibrary />}
      </Content>

      {/* Modals */}
      {showTemplateDesigner && (
        <Modal
          title={selectedTemplate ? `Edit Template: ${selectedTemplate.name}` : 'Create New Template'}
          open={showTemplateDesigner}
          onCancel={handleTemplateDesignerClose}
          width="90%"
          style={{ maxWidth: 1200 }}
          bodyStyle={{ padding: 0, height: '80vh' }}
          footer={null}
        >
          <TemplateDesigner
            selectedTemplate={selectedTemplate}
            onClose={handleTemplateDesignerClose}
            onSave={() => {
              handleTemplateDesignerClose();
              message.success(selectedTemplate ? 'Template updated successfully' : 'Template created successfully');
            }}
          />
        </Modal>
      )}

      {showClassModal && <CreateTicketClassModal />}
      {showViewModal && <ViewTicketClassModal />}
    </Layout>
  );
};

export default TicketManagementUI;





// import React, { useState, useEffect } from 'react';
// import {
//   FileTextOutlined,
//   PlusOutlined,
//   EditOutlined,
//   DeleteOutlined,
//   CopyOutlined,
//   EyeOutlined,
//   SaveOutlined,
//   SettingOutlined,
//   UserOutlined,
//   CalendarOutlined,
//   CheckOutlined,
//   SearchOutlined,
//   FilterOutlined,
//   DownloadOutlined,
//   QrcodeOutlined,
//   PictureOutlined,
//   FontSizeOutlined,
//   HighlightOutlined,
//   ArrowDownOutlined,
//   ArrowRightOutlined,
//   ExclamationCircleOutlined,
//   BgColorsOutlined,
//   LockOutlined,
//   BoldOutlined,
//   ItalicOutlined,
//   UnderlineOutlined,
//   StrikethroughOutlined
// } from '@ant-design/icons';

// import {
//   Layout,
//   Card,
//   Button,
//   Input,
//   Table,
//   Tag,
//   Progress,
//   Modal,
//   Steps,
//   Radio,
//   Checkbox,
//   Select,
//   DatePicker,
//   Tabs,
//   Row,
//   Col,
//   Statistic,
//   List,
//   Avatar,
//   Divider,
//   Space,
//   Tooltip,
//   Form,
//   InputNumber,
//   ColorPicker,
//   message,
//   Typography
// } from 'antd';
// import TemplateDesigner from './TemplateDesign';
// import { getTicketTemplateApi } from '../../../redux/api/api';
// import BadgeTemplatesGallery from './BadgeTemplatesGallery';

// const { Header, Content } = Layout;
// const { TextArea } = Input;
// const { Option } = Select;
// const { RangePicker } = DatePicker;
// const { TabPane } = Tabs;
// const { Step } = Steps;

// const TicketManagementUI = ({ onTicketDataChange, initialTicketClasses = [] }) => {
//   const [activeView, setActiveView] = useState('classes');
//   const [showTemplateDesigner, setShowTemplateDesigner] = useState(false);
//   const [showClassModal, setShowClassModal] = useState(false);
//   const [selectedTemplate, setSelectedTemplate] = useState(null);
//   const [searchQuery, setSearchQuery] = useState('');
//   const [form] = Form.useForm();
//   const [assignmentType, setAssignmentType] = useState('role_based');
//   const [showViewModal, setShowViewModal] = useState(false);
//   const [isEditing, setIsEditing] = useState(false);
//   const [ticketClasses, setTicketClasses] = useState(initialTicketClasses);
//   const { Title, Text } = Typography;
//   const [selectedTicketClass, setSelectedTicketClass] = useState(null);

//   const [templates, setTemplates] = useState([]);
//   // //console.log(templates)
//   const [loading, setLoading] = useState(false);

//   const useTicketClasses = (eventId) => {
//     return {
//       createTicketClass: async (data) => {
//         //console.log('Creating ticket class:', data);
//         // Replace with actual API call
//         return Promise.resolve();
//       }
//     };
//   };

//   const eventId = 'event-123'; // Mock event ID
//   const { createTicketClass } = useTicketClasses(eventId);
//   useEffect(() => {
//     if (onTicketDataChange) {
//       onTicketDataChange(ticketClasses);
//     }
//   }, [ticketClasses, onTicketDataChange]);
//   // Sample data


//   const fetchTemplates = async () => {
//     setLoading(true);
//     try {
//       const { data } = await getTicketTemplateApi();;
//       // console.log("response", response)
//       // const data = await response.json();
//       //console.log(data)
//       if (data.success) {
//         setTemplates(data.templates);
//       } else {
//         console.error('Failed to fetch templates:', data.message);
//       }
//     } catch (error) {
//       console.error('Error fetching templates:', error);
//     } finally {
//       setLoading(false);
//     }
//   };
//   // Fetch templates on component mount
//   useEffect(() => {
//     fetchTemplates();
//   }, []);

//   const handleEditTemplate = (template) => {
//     console.log('Editing template:', template);
//     setSelectedTemplate(template);
//     setShowTemplateDesigner(true);
//   };

//   // Handle template creation
//   const handleCreateTemplate = () => {
//     setSelectedTemplate(null);
//     setShowTemplateDesigner(true);
//   };

//   // Handle template designer close
//   const handleTemplateDesignerClose = () => {
//     setShowTemplateDesigner(false);
//     setSelectedTemplate(null);
//     // Refresh templates if needed
//     fetchTemplates();
//   };


//   const TemplateLibrary = () => (
//     <div style={{ padding: '24px 0' }}>
//       <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
//         <Col flex="auto">
//           <Input
//             placeholder="Search templates..."
//             prefix={<SearchOutlined />}
//             value={searchQuery}
//             onChange={(e) => setSearchQuery(e.target.value)}
//             size="large"
//           />
//         </Col>
//         <Col>
//           <Button icon={<FilterOutlined />} size="large">
//             Filter
//           </Button>
//         </Col>
//         <Col>
//           <Button
//             type="primary"
//             icon={<PlusOutlined />}
//             onClick={handleCreateTemplate}
//             size="large"
//           >
//             Create Template
//           </Button>
//         </Col>
//       </Row>

//       <BadgeTemplatesGallery
//         onEditTemplate={handleEditTemplate}
//         onDuplicateTemplate={() => { }}
//         onDeleteTemplate={() => { }} />
//     </div>
//   );

//   const handleCreateTicketClass = (formData) => {
//     const selectedTemplate = templates.find(t => t.id === formData.templateId);

//     const newTicketClass = {
//       id: Date.now(),
//       name: formData.name,
//       description: formData.description,
//       templateName: selectedTemplate?.name || 'No Template',
//       templateId: formData.templateId,
//       assignmentType: formData.assignmentRule.type,
//       roles: formData.assignmentRule.roles,
//       programs: formData.assignmentRule.programs,
//       grantAllPrograms: formData.assignmentRule.grantAllPrograms,
//       price: formData.price,
//       quantity: formData.quantity,
//       sold: 0,
//       status: 'active',
//       salesSettings: formData.salesSettings,
//       benefits: formData.benefits,
//       requireApproval: formData.requireApproval,
//       createdAt: new Date().toISOString()
//     };

//     const updatedClasses = [...ticketClasses, newTicketClass];
//     updateTicketClasses(updatedClasses);
//     message.success('Ticket class created successfully');
//   };

//   const updateTicketClasses = (updatedClasses) => {
//     setTicketClasses(updatedClasses);
//   };


//   const handleViewTicketClass = (ticketClass) => {
//     setSelectedTicketClass(ticketClass);
//     setShowViewModal(true);
//   };

//   const handleDeleteTicketClass = (ticketClass) => {
//     Modal.confirm({
//       title: 'Delete Ticket Class',
//       content: `Are you sure you want to delete "${ticketClass.name}"? This action cannot be undone.`,
//       okText: 'Delete',
//       okType: 'danger',
//       cancelText: 'Cancel',
//       onOk() {
//         const updatedClasses = ticketClasses.filter(tc => tc.id !== ticketClass.id);
//         setTicketClasses(updatedClasses);
//         if (onTicketDataChange) {
//           onTicketDataChange(updatedClasses);
//         }
//         message.success('Ticket class deleted successfully');
//       }
//     });
//   };

//   const handleEditClick = (ticketClass) => {
//     setSelectedTicketClass(ticketClass);
//     setIsEditing(true);
//     setShowClassModal(true);

//     // Pre-fill form with existing data
//     form.setFieldsValue({
//       name: ticketClass.name,
//       description: ticketClass.description,
//       color: ticketClass.color,
//       templateId: ticketClass.templateId,
//       price: ticketClass.price,
//       quantity: ticketClass.quantity,
//       assignmentRule: {
//         type: ticketClass.assignmentType,
//         roles: ticketClass.roles,
//         programs: ticketClass.programs,
//         grantAllPrograms: ticketClass.grantAllPrograms
//       },
//       salesSettings: ticketClass.salesSettings,
//       benefits: ticketClass.benefits,
//       requireApproval: ticketClass.requireApproval
//     });
//     setAssignmentType(ticketClass.assignmentType);
//   };


//   // Ticket Classes View
//   const TicketClasses = () => {
//     const columns = [
//       {
//         title: 'Ticket Class',
//         dataIndex: 'name',
//         key: 'name',
//         render: (text) => <div style={{ fontWeight: 500 }}>{text}</div>
//       },
//       {
//         title: 'Template',
//         dataIndex: 'templateName',
//         key: 'templateName',
//         render: (text) => (
//           <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
//             <FileTextOutlined style={{ color: '#999' }} />
//             <span>{text}</span>
//           </div>
//         )
//       },
//       {
//         title: 'Assignment',
//         key: 'assignment',
//         render: (record) => (
//           <div>
//             {record.assignmentType === 'role_based' ? (
//               <div>
//                 <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#666', marginBottom: 8 }}>
//                   <UserOutlined />
//                   <span style={{ fontSize: '12px' }}>Role-based</span>
//                 </div>
//                 <div>
//                   {record.roles?.map((role, idx) => (
//                     <Tag key={idx} color="purple" style={{ marginBottom: 4 }}>{role}</Tag>
//                   ))}
//                 </div>
//               </div>
//             ) : (
//               <div>
//                 <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#666', marginBottom: 8 }}>
//                   <CalendarOutlined />
//                   <span style={{ fontSize: '12px' }}>Program-based</span>
//                 </div>
//                 <div>
//                   {record.programs?.map((program, idx) => (
//                     <Tag key={idx} color="blue" style={{ marginBottom: 4 }}>{program}</Tag>
//                   ))}
//                 </div>
//               </div>
//             )}
//           </div>
//         )
//       },
//       {
//         title: 'Price',
//         dataIndex: 'price',
//         key: 'price',
//         render: (price) => (
//           <span style={{ fontWeight: 500 }}>
//             {price === 0 ? 'Free' : `₹${price}`}
//           </span>
//         )
//       },
//       {
//         title: 'Sales',
//         key: 'sales',
//         render: (record) => (
//           <div>
//             <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
//               <span>{record.sold}/{record.quantity}</span>
//               <span>{Math.round((record.sold / record.quantity) * 100)}%</span>
//             </div>
//             <Progress
//               percent={Math.round((record.sold / record.quantity) * 100)}
//               size="small"
//               showInfo={false}
//             />
//           </div>
//         )
//       },
//       {
//         title: 'Status',
//         dataIndex: 'status',
//         key: 'status',
//         render: (status) => (
//           <Tag color="green">{status}</Tag>
//         )
//       },
//       {
//         title: 'Actions',
//         key: 'actions',
//         render: (record) => (
//           <Space>
//             <Tooltip title="View">
//               <Button
//                 icon={<EyeOutlined />}
//                 size="small"
//                 onClick={() => handleViewTicketClass(record)}
//               />
//             </Tooltip>
//             <Tooltip title="Edit">
//               <Button
//                 icon={<EditOutlined />}
//                 size="small"
//                 onClick={() => handleEditClick(record)}
//               />
//             </Tooltip>
//             <Tooltip title="Delete">
//               <Button
//                 icon={<DeleteOutlined />}
//                 size="small"
//                 danger
//                 onClick={() => handleDeleteTicketClass(record)}
//               />
//             </Tooltip>
//           </Space>
//         )
//       }
//     ];

//     return (
//       <div style={{ padding: '24px 0' }}>
//         <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
//           <Col>
//             {/* <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Ticket Classes</h2>
//             <p style={{ color: '#666', margin: '4px 0 0 0' }}>Configure tickets for your event with templates and roles</p> */}
//           </Col>
//           <Col>
//             <Button
//               type="primary"
//               icon={<PlusOutlined />}
//               onClick={() => setShowClassModal(true)}
//               size="large"
//             >
//               Create Ticket Class
//             </Button>
//           </Col>
//         </Row>

//         <Table
//           columns={columns}
//           dataSource={ticketClasses}
//           rowKey="id"
//           pagination={false}
//         />
//       </div>
//     );
//   };

//   // Create Ticket Class Modal
  // const CreateTicketClassModal = ({ onCreateTicketClass, onCancel }) => {
  //   const [step, setStep] = useState(1);
  //   const [formError, setFormError] = useState('');
  //   const [loading, setLoading] = useState(false);
  //   const [formData, setFormData] = useState({
  //     // Step 1: Basic Information
  //     name: '',
  //     description: '',
  //     templateId: '',
  //     price: 0,
  //     quantity: 100,

  //     // Step 2: Assignment Rules
  //     assignmentRule: {
  //       type: 'role_based',
  //       roles: [],
  //       programs: [],
  //       grantAllPrograms: false
  //     },

  //     // Step 3: Additional Settings
  //     salesSettings: {
  //       visibility: 'always',
  //       minPurchase: 1,
  //       maxPurchase: 5,
  //       startDate: null,
  //       endDate: null,
  //       showWhenSoldOut: false,
  //       enableWaitlist: false
  //     },
  //     benefits: [],
  //     requireApproval: false
  //   });

  //   const updateFormData = (field, value) => {
  //     setFormData(prev => ({ ...prev, [field]: value }));
  //   };

  //   const updateNestedFormData = (parent, updates) => {
  //     setFormData(prev => ({
  //       ...prev,
  //       [parent]: { ...prev[parent], ...updates }
  //     }));
  //   };


  //   const validateStep = () => {
  //     if (step === 1) {
  //       if (!formData.name.trim()) return { isValid: false, error: 'Name is required' };
  //       // if (!formData.templateId) return { isValid: false, error: 'Template is required' };
  //     }
  //     if (step === 2) {
  //       if (formData.assignmentRule.type === 'role_based' && formData.assignmentRule.roles.length === 0) {
  //         // return { isValid: false, error: 'Select at least one role' };
  //       }
  //     }
  //     return { isValid: true };
  //   };

  //   const resetForm = () => {
  //     setFormData({
  //       name: '',
  //       description: '',
  //       templateId: '',
  //       price: 0,
  //       quantity: 100,
  //       assignmentRule: {
  //         type: 'role_based',
  //         roles: [],
  //         programs: [],
  //         grantAllPrograms: false
  //       },
  //       salesSettings: {
  //         visibility: 'always',
  //         minPurchase: 1,
  //         maxPurchase: 5,
  //         startDate: null,
  //         endDate: null,
  //         showWhenSoldOut: false,
  //         enableWaitlist: false
  //       },
  //       benefits: [],
  //       requireApproval: false
  //     });
  //   };

  //   const handleNext = () => {
  //     const validation = validateStep(step);
  //     if (!validation.isValid) {
  //       setFormError(validation.error);
  //       return;
  //     }
  //     setFormError('');
  //     setStep(step + 1);
  //   };

  //   const handleBack = () => {
  //     if (step > 1) {
  //       setStep(step - 1);
  //       setFormError('');
  //     } else {
  //       handleCancel();
  //     }
  //   };

  //   const handleCancel = () => {
  //     setShowClassModal(false);
  //     setStep(1);
  //     setFormError('');
  //     resetForm();
  //   };

  //   const handleSubmit = () => {
  //     const validation = validateStep();
  //     if (!validation.isValid) {
  //       setFormError(validation.error);
  //       return;
  //     }
  //     handleCreateTicketClass(formData);
  //     setShowClassModal(false);
  //     setStep(1);
  //     setFormError('');
  //   };



  //   const steps = [
  //     {
  //       title: 'Basic Info',
  //       content: (
  //         <Form layout="vertical">
  //           <Form.Item label="Ticket Name" required>
  //             <Input
  //               placeholder="Enter ticket name"
  //               value={formData.name}
  //               onChange={(e) => updateFormData('name', e.target.value)}
  //               size="large"
  //             />
  //           </Form.Item>

  //           <Form.Item label="Color">
  //             <ColorPicker
  //               value={formData.color}
  //               onChange={(color) => updateFormData('color', color.toHexString())}
  //               showText
  //               style={{ width: '100%' }}
  //             />
  //           </Form.Item>

  //           <div style={{ marginBottom: 16 }}>
  //             <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Quantity *</label>
  //             <InputNumber
  //               value={formData.quantity}
  //               onChange={(value) => updateFormData('quantity', value || 0)}
  //               style={{ width: '100%' }}
  //               min={1}
  //               size="large"
  //             />
  //           </div>

  //           <div style={{ marginBottom: 16 }}>
  //             <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Price</label>
  //             <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
  //               <Radio.Group
  //                 value={formData.price === 0 ? 'free' : 'paid'}
  //                 onChange={(e) => updateFormData('price', e.target.value === 'free' ? 0 : 100)}
  //               >
  //                 <Radio value="free">Free</Radio>
  //                 <Radio value="paid">Paid</Radio>
  //               </Radio.Group>
  //               {formData.price > 0 && (
  //                 <InputNumber
  //                   value={formData.price}
  //                   onChange={(value) => updateFormData('price', value || 0)}
  //                   prefix="₹"
  //                   style={{ width: 120 }}
  //                 />
  //               )}
  //             </div>
  //           </div>

  //           <Form.Item label="Select Template" >
  //             <Select
  //               placeholder="Choose a template..."
  //               value={formData.templateId}
  //               onChange={(value) => updateFormData('templateId', value)}
  //               size="large"
  //             >
  //               {templates.map(t => (
  //                 <Option key={t.id} value={t.id}>{t.name}</Option>
  //               ))}
  //             </Select>
  //           </Form.Item>
  //         </Form>
  //       )
  //     },
  //     {
  //       title: 'Settings',
  //       content: (
  //         <Form layout="vertical">
  //           {/* General Visibility Offers Section */}
  //           <div style={{
  //             background: '#f8f9fa',
  //             padding: 16,
  //             borderRadius: 8,
  //             marginBottom: 16,
  //             border: '1px solid #e9ecef'
  //           }}>
  //             <h4 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
  //               <EyeOutlined />
  //               General Visibility Offers 😊
  //             </h4>

  //             <Form.Item label="Ticket Visibility">
  //               <Radio.Group
  //                 value={formData.salesSettings.visibility}
  //                 onChange={(e) => updateNestedFormData('salesSettings', { visibility: e.target.value })}
  //               >
  //                 <Space direction="vertical">
  //                   <Radio value="always">Always Visible</Radio>
  //                   <Radio value="hidden">Always Hidden</Radio>
  //                   <Radio value="conditional">Hide When</Radio>
  //                 </Space>
  //               </Radio.Group>
  //             </Form.Item>

  //             {formData.salesSettings.visibility === 'conditional' && (
  //               <Form.Item>
  //                 <Checkbox.Group
  //                   value={formData.salesSettings.visibilityOptions}
  //                   onChange={(values) => updateNestedFormData('salesSettings', { visibilityOptions: values })}
  //                 >
  //                   <Space direction="vertical">
  //                     <Checkbox value="salesNotStarted">Ticket sales haven't begun</Checkbox>
  //                     <Checkbox value="soldOut">Ticket sales are sold out or marked as sold out</Checkbox>
  //                     <Checkbox value="salesEnded">Ticket sales have ended</Checkbox>
  //                     <Checkbox value="statusClosed">Ticket class status is closed</Checkbox>
  //                   </Space>
  //                 </Checkbox.Group>
  //               </Form.Item>
  //             )}
  //           </div>

  //           {/* Registration Approval Section */}
  //           <div style={{
  //             background: '#f8f9fa',
  //             padding: 16,
  //             borderRadius: 8,
  //             marginBottom: 16,
  //             border: '1px solid #e9ecef'
  //           }}>
  //             <h4 style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
  //               <UserOutlined />
  //               Registration approval 💬
  //             </h4>
  //             <p style={{ color: '#666', marginBottom: 12 }}>
  //               You can pre-screen the order request placed by the visitors and manually approve or deny them before issuing their tickets.
  //             </p>
  //             <Checkbox
  //               checked={formData.salesSettings.requireApproval}
  //               onChange={(e) => updateNestedFormData('salesSettings', { requireApproval: e.target.checked })}
  //             >
  //               Require approval for registration
  //             </Checkbox>
  //           </div>

  //           {/* Purchase Limits */}
  //           <Row gutter={16}>
  //             <Col span={12}>
  //               <Form.Item label="Minimum Buying Limit *">
  //                 <InputNumber
  //                   value={formData.salesSettings.minPurchase}
  //                   onChange={(value) => updateNestedFormData('salesSettings', { minPurchase: value || 1 })}
  //                   style={{ width: '100%' }}
  //                   min={1}
  //                   size="large"
  //                 />
  //               </Form.Item>
  //             </Col>
  //             <Col span={12}>
  //               <Form.Item label="Maximum Buying Limit *">
  //                 <InputNumber
  //                   value={formData.salesSettings.maxPurchase}
  //                   onChange={(value) => updateNestedFormData('salesSettings', { maxPurchase: value || 1 })}
  //                   style={{ width: '100%' }}
  //                   min={1}
  //                   size="large"
  //                 />
  //               </Form.Item>
  //             </Col>
  //           </Row>

  //           {/* Sales Dates */}
  //           <Row gutter={16}>
  //             <Col span={12}>
  //               <Form.Item label="Sales Start Date">
  //                 <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
  //                   <DatePicker
  //                     value={formData.salesSettings.startDate}
  //                     onChange={(date) => updateNestedFormData('salesSettings', { startDate: date })}
  //                     style={{ flex: 1 }}
  //                     placeholder="Select start date"
  //                   />
  //                   <Radio.Group
  //                     value={formData.salesSettings.startDate ? 'custom' : 'now'}
  //                     onChange={(e) => updateNestedFormData('salesSettings', {
  //                       startDate: e.target.value === 'now' ? new Date() : null
  //                     })}
  //                   >
  //                     <Radio value="now">Now</Radio>
  //                   </Radio.Group>
  //                 </div>
  //               </Form.Item>
  //             </Col>
  //             <Col span={12}>
  //               <Form.Item label="Sales End Date">
  //                 <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
  //                   <DatePicker
  //                     value={formData.salesSettings.endDate}
  //                     onChange={(date) => updateNestedFormData('salesSettings', { endDate: date })}
  //                     style={{ flex: 1 }}
  //                     placeholder="Select end date"
  //                   />
  //                   <Radio.Group
  //                     value={formData.salesSettings.endDate ? 'custom' : 'eventEnd'}
  //                     onChange={(e) => updateNestedFormData('salesSettings', {
  //                       endDate: e.target.value === 'eventEnd' ? null : null
  //                     })}
  //                   >
  //                     <Radio value="eventEnd">Event End Date</Radio>
  //                   </Radio.Group>
  //                 </div>
  //               </Form.Item>
  //             </Col>
  //           </Row>

  //           {/* Sales Times */}
  //           <Row gutter={16}>
  //             <Col span={12}>
  //               <Form.Item label="Start Time">
  //                 <Select
  //                   value={formData.salesSettings.startTime}
  //                   onChange={(value) => updateNestedFormData('salesSettings', { startTime: value })}
  //                   size="large"
  //                 >
  //                   <Option value="12:00 AM">12:00 AM</Option>
  //                   <Option value="1:00 AM">1:00 AM</Option>
  //                   <Option value="2:00 AM">2:00 AM</Option>
  //                   <Option value="3:00 AM">3:00 AM</Option>
  //                   <Option value="4:00 AM">4:00 AM</Option>
  //                   <Option value="5:00 AM">5:00 AM</Option>
  //                   <Option value="6:00 AM">6:00 AM</Option>
  //                   <Option value="7:00 AM">7:00 AM</Option>
  //                   <Option value="8:00 AM">8:00 AM</Option>
  //                   <Option value="9:00 AM">9:00 AM</Option>
  //                   <Option value="10:00 AM">10:00 AM</Option>
  //                   <Option value="11:00 AM">11:00 AM</Option>
  //                   <Option value="12:00 PM">12:00 PM</Option>
  //                 </Select>
  //               </Form.Item>
  //             </Col>
  //             <Col span={12}>
  //               <Form.Item label="End Time">
  //                 <Select
  //                   value={formData.salesSettings.endTime}
  //                   onChange={(value) => updateNestedFormData('salesSettings', { endTime: value })}
  //                   size="large"
  //                 >
  //                   <Option value="12:00 PM">12:00 PM</Option>
  //                   <Option value="1:00 PM">1:00 PM</Option>
  //                   <Option value="2:00 PM">2:00 PM</Option>
  //                   <Option value="3:00 PM">3:00 PM</Option>
  //                   <Option value="4:00 PM">4:00 PM</Option>
  //                   <Option value="5:00 PM">5:00 PM</Option>
  //                   <Option value="6:00 PM">6:00 PM</Option>
  //                   <Option value="7:00 PM">7:00 PM</Option>
  //                   <Option value="8:00 PM">8:00 PM</Option>
  //                   <Option value="9:00 PM">9:00 PM</Option>
  //                   <Option value="10:00 PM">10:00 PM</Option>
  //                   <Option value="11:00 PM">11:00 PM</Option>
  //                 </Select>
  //               </Form.Item>
  //             </Col>
  //           </Row>

  //           {/* Description */}
  //           <Form.Item label="Description">
  //             <TextArea
  //               rows={4}
  //               placeholder="Enter ticket description..."
  //               value={formData.description}
  //               onChange={(e) => updateFormData('description', e.target.value)}
  //               style={{
  //                 border: '1px solid #d9d9d9',
  //                 borderRadius: 6
  //               }}
  //             />
  //             <div style={{ marginTop: 8, display: 'flex', gap: 4 }}>
  //               <Button type="text" icon={<BoldOutlined />} />
  //               <Button type="text" icon={<ItalicOutlined />} />
  //               <Button type="text" icon={<UnderlineOutlined />} />
  //               <Button type="text" icon={<StrikethroughOutlined />} />
  //               <Button type="text" icon={<FontSizeOutlined />} />
  //               <Button type="text" icon={<BgColorsOutlined />} />
  //             </div>
  //           </Form.Item>
  //         </Form>
  //       )
  //     },
  //     {
  //       title: 'Assign',
  //       content: (
  //         <Form layout="vertical">
  //           <Form.Item label="How should this ticket be assigned?" required>
  //             <Radio.Group
  //               value={assignmentType}
  //               onChange={(e) => setAssignmentType(e.target.value)}
  //               style={{ width: '100%' }}
  //             >
  //               <Space direction="vertical" style={{ width: '100%' }}>
  //                 <Radio value="role_based" style={{ width: '100%', padding: 16, border: '1px solid #d9d9d9', borderRadius: 6 }}>
  //                   <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
  //                     <UserOutlined style={{ color: '#1890ff', fontSize: 16, marginTop: 2 }} />
  //                     <div>
  //                       <div style={{ fontWeight: 500 }}>Role-Based</div>
  //                       <div style={{ color: '#666', fontSize: 12 }}>
  //                         Grant access to all programs for specific roles (e.g., VIP, Speaker, Staff)
  //                       </div>
  //                     </div>
  //                   </div>
  //                 </Radio>
  //                 <Radio value="program_based" style={{ width: '100%', padding: 16, border: '1px solid #d9d9d9', borderRadius: 6 }}>
  //                   <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
  //                     <CalendarOutlined style={{ color: '#52c41a', fontSize: 16, marginTop: 2 }} />
  //                     <div>
  //                       <div style={{ fontWeight: 500 }}>Program-Based</div>
  //                       <div style={{ color: '#666', fontSize: 12 }}>
  //                         Grant access to specific programs/sessions only
  //                       </div>
  //                     </div>
  //                   </div>
  //                 </Radio>
  //               </Space>
  //             </Radio.Group>
  //           </Form.Item>

  //           {assignmentType === 'role_based' && (
  //             <Form.Item label="Select Roles" required>
  //               <Checkbox.Group style={{ width: '100%' }}>
  //                 <Row gutter={[8, 8]}>
  //                   {['VIP', 'Speaker', 'Sponsor', 'Staff', 'Volunteer', 'Guest'].map(role => (
  //                     <Col span={8} key={role}>
  //                       <Checkbox value={role}>{role}</Checkbox>
  //                     </Col>
  //                   ))}
  //                 </Row>
  //               </Checkbox.Group>
  //               <Checkbox style={{ marginTop: 16 }}>
  //                 Grant access to all programs in the event
  //               </Checkbox>
  //             </Form.Item>
  //           )}

  //           {assignmentType === 'program_based' && (
  //             <Form.Item label="Select Programs" required>
  //               <Checkbox.Group style={{ width: '100%' }}>
  //                 <Row gutter={[8, 8]}>
  //                   {['Main Conference', 'Workshop A', 'Workshop B', 'Networking Dinner', 'Closing Ceremony'].map(program => (
  //                     <Col span={12} key={program}>
  //                       <Checkbox value={program}>{program}</Checkbox>
  //                     </Col>
  //                   ))}
  //                 </Row>
  //               </Checkbox.Group>
  //             </Form.Item>
  //           )}
  //         </Form>
  //       )
  //     },
  //   ];


  //   return (
  //     <Modal
  //       title="Create Ticket Class"
  //       open={showClassModal}
  //       onCancel={() => {
  //         setShowClassModal(false);
  //         setStep(1);
  //         setFormError('');
  //       }}
  //       width={800}
  //       footer={[
  //         <Button key="back" onClick={() => step > 1 ? setStep(step - 1) : setShowClassModal(false)}>
  //           {step === 1 ? 'Cancel' : 'Back'}
  //         </Button>,
  //         <Button
  //           key="next"
  //           type="primary"
  //           onClick={step < 3 ? handleNext : handleSubmit}
  //         >
  //           {step === 3 ? 'Create' : 'Next'}
  //         </Button>
  //       ]}
  //     >
  //       <Steps current={step - 1} style={{ marginBottom: 24 }}>
  //         {steps.map(item => (
  //           <Step key={item.title} title={item.title} />
  //         ))}
  //       </Steps>
  //       {formError && (
  //         <div style={{ color: '#ff4d4f', marginBottom: 16 }}>{formError}</div>
  //       )}
  //       <div style={{ minHeight: 400 }}>
  //         {steps[step - 1].content}
  //       </div>
  //     </Modal>
  //   );
  // };

//   return (
//     <Layout style={{ minHeight: '20vh', background: '#f5f5f5' }}>
//       {/* Navigation Tabs */}
//       <div style={{ background: '#fff', borderBottom: '1px solid #f0f0f0' }}>
//         <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
//           <Tabs
//             activeKey={activeView}
//             onChange={setActiveView}
//             items={[

//               {
//                 key: 'classes',
//                 label: (
//                   <span>
//                     <SettingOutlined style={{ marginRight: 8 }} />
//                     Ticket Classes
//                   </span>
//                 )
//               },
//               {
//                 key: 'templates',
//                 label: (
//                   <span>
//                     <FileTextOutlined style={{ marginRight: 8 }} />
//                     Ticket Templates
//                   </span>
//                 )
//               },
//             ]}
//           />
//         </div>
//       </div>

//       {/* Main Content */}
//       <Content style={{ padding: '0 24px', maxWidth: 1200, margin: '0 auto', width: '100%' }}>
//         {activeView === 'classes' && <TicketClasses />}
//         {activeView === 'templates' && <TemplateLibrary
//           setShowTemplateDesigner={setShowTemplateDesigner}
//           setSelectedTemplate={setSelectedTemplate}
//         />}
//       </Content>

//       {/* Modals */}
//       {showTemplateDesigner && (

//         <Modal
//           title={selectedTemplate ? `Edit Template: ${selectedTemplate.name}` : 'Create New Template'}
//           open={showTemplateDesigner}
//           onCancel={handleTemplateDesignerClose}
//           width="90%"
//           style={{ maxWidth: 1200 }}
//           bodyStyle={{ padding: 0, height: '80vh' }}
//           footer={null} // Remove default footer if TemplateDesigner has its own
//         >
//           <TemplateDesigner
//             selectedTemplate={selectedTemplate}
//             onClose={handleTemplateDesignerClose}
//             // onSave={() => {
//             //   handleTemplateDesignerClose();
//             //   fetchTemplates(); // Refresh the templates list
//             // }}
//             onSave={() => {
//               handleTemplateDesignerClose();
//               message.success(selectedTemplate ? 'Template updated successfully' : 'Template created successfully');
//             }}
//           />
//         </Modal>
//       )}
//       {showClassModal && <CreateTicketClassModal />}
//     </Layout>
//   );
// };

// export default TicketManagementUI;





